/**
 * Extension Bridge
 *
 * Handles communication between the ShowLark Visualizer and the
 * ShowLark Suno Extension via window.postMessage.
 *
 * The extension injects a content script that listens for these messages
 * and forwards them to the extension's background script.
 */

// Message timeout (10 seconds)
const MESSAGE_TIMEOUT = 10000;

// Extension detection timeout (3 seconds)
const DETECTION_TIMEOUT = 3000;

// Connection status constants
const CONNECTION_STATUS = {
  UNKNOWN: 'unknown',
  CONNECTED: 'connected',
  CONTEXT_INVALIDATED: 'context_invalidated'
};

// State
let extensionAvailable = null;
let extensionVersion = null;
let messageHandlers = new Map();
let progressCallback = null;

// Connection status state
let connectionStatus = CONNECTION_STATUS.UNKNOWN;
let connectionStatusDetails = {};
let connectionStatusCallbacks = new Set();

/**
 * Generate unique request ID
 */
function generateRequestId() {
  return `req_${Date.now()}_${Math.random().toString(36).substring(7)}`;
}

/**
 * Send a message to the extension and wait for response
 */
function sendMessage(type, data = {}) {
  return new Promise((resolve, reject) => {
    const requestId = generateRequestId();

    // Set up timeout
    const timeout = setTimeout(() => {
      messageHandlers.delete(requestId);
      reject(new Error('Extension response timeout'));
    }, MESSAGE_TIMEOUT);

    // Set up response handler
    messageHandlers.set(requestId, (response) => {
      clearTimeout(timeout);
      messageHandlers.delete(requestId);

      if (response.error) {
        reject(new Error(response.error));
      } else {
        resolve(response);
      }
    });

    // Send message
    window.postMessage({
      type,
      ...data,
      _requestId: requestId
    }, '*');
  });
}

/**
 * Handle incoming messages from the extension
 */
function handleMessage(event) {
  // Only accept messages from same window
  if (event.source !== window) return;

  const message = event.data;

  // Only handle ShowLark messages that are responses
  if (!message || !message.type || !message.type.startsWith('SHOWLARK_')) return;
  if (!message._isResponse) return;

  // Handle response to a pending request
  if (message._requestId && messageHandlers.has(message._requestId)) {
    const handler = messageHandlers.get(message._requestId);
    handler(message);
    return;
  }

  // Handle broadcast messages
  switch (message.type) {
    case 'SHOWLARK_EXTENSION_READY':
      extensionAvailable = true;
      extensionVersion = message.version;
      connectionStatus = CONNECTION_STATUS.CONNECTED;
      connectionStatusDetails = { version: message.version };
      console.log('[ExtensionBridge] Extension detected, version:', extensionVersion);

      // Dispatch event for listeners
      document.dispatchEvent(new CustomEvent('showlarkExtensionReady', {
        detail: { version: extensionVersion }
      }));

      // Notify connection status listeners
      notifyConnectionStatusChange();
      break;

    case 'SHOWLARK_CONNECTION_STATUS':
      handleConnectionStatusChange(message);
      break;

    case 'SHOWLARK_GENERATION_PROGRESS':
      if (progressCallback) {
        progressCallback(message.task);
      }
      // Also dispatch event
      document.dispatchEvent(new CustomEvent('showlarkGenerationProgress', {
        detail: { task: message.task }
      }));
      break;

    case 'SHOWLARK_PUSH_SONGS':
      // Songs pushed from extension popup
      document.dispatchEvent(new CustomEvent('showlarkPushSongs', {
        detail: { songs: message.songs }
      }));
      break;

    case 'SHOWLARK_EXPORT_PROGRESS':
      // Export progress from batch downloads
      document.dispatchEvent(new CustomEvent('showlarkExportProgress', {
        detail: {
          current: message.current,
          total: message.total,
          song: message.song
        }
      }));
      break;
  }
}

// Set up message listener
window.addEventListener('message', handleMessage);

/**
 * Handle connection status change from content script
 */
function handleConnectionStatusChange(message) {
  const previousStatus = connectionStatus;
  connectionStatus = message.status;
  connectionStatusDetails = message.details || {};

  console.log('[ExtensionBridge] Connection status changed:', connectionStatus, connectionStatusDetails);

  // If context invalidated, mark extension as unavailable
  if (connectionStatus === CONNECTION_STATUS.CONTEXT_INVALIDATED) {
    extensionAvailable = false;
  }

  // If reconnected, reset the cached availability flag and notify listeners
  if (connectionStatus === CONNECTION_STATUS.CONNECTED) {
    extensionAvailable = true;
    extensionVersion = connectionStatusDetails.version || extensionVersion;

    // Dispatch event so listeners (library-browser, playlist-generator) can recover
    document.dispatchEvent(new CustomEvent('showlarkExtensionReady', {
      detail: { version: extensionVersion }
    }));
  }

  // Dispatch CustomEvent for any listeners
  document.dispatchEvent(new CustomEvent('showlarkConnectionStatus', {
    detail: {
      status: connectionStatus,
      details: connectionStatusDetails,
      previousStatus,
      connected: connectionStatus === CONNECTION_STATUS.CONNECTED
    }
  }));

  // Notify registered callbacks
  notifyConnectionStatusChange();
}

/**
 * Notify all registered connection status callbacks
 */
function notifyConnectionStatusChange() {
  const connected = connectionStatus === CONNECTION_STATUS.CONNECTED;
  for (const callback of connectionStatusCallbacks) {
    try {
      callback(connectionStatus, connectionStatusDetails, connected);
    } catch (e) {
      console.error('[ExtensionBridge] Error in connection status callback:', e);
    }
  }
}

/**
 * Extension Bridge API
 */
export const extensionBridge = {
  /**
   * Check if the extension is available
   * @returns {Promise<boolean>}
   */
  async isAvailable() {
    // If already detected, return cached result
    if (extensionAvailable === true) {
      return true;
    }

    // Check for window.ShowLarkExtension (set by content script)
    if (window.ShowLarkExtension?.isAvailable) {
      extensionAvailable = true;
      extensionVersion = window.ShowLarkExtension.version;
      return true;
    }

    // Send a ping and wait for response
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        window.removeEventListener('message', handler);
        extensionAvailable = false;
        resolve(false);
      }, DETECTION_TIMEOUT);

      const handler = (event) => {
        if (event.data?.type === 'SHOWLARK_EXTENSION_STATUS' ||
            event.data?.type === 'SHOWLARK_EXTENSION_READY') {
          clearTimeout(timeout);
          window.removeEventListener('message', handler);
          extensionAvailable = true;
          extensionVersion = event.data.version;
          resolve(true);
        }
      };

      window.addEventListener('message', handler);

      window.postMessage({
        type: 'SHOWLARK_REQUEST_EXTENSION_STATUS'
      }, '*');
    });
  },

  /**
   * Get extension version
   */
  getVersion() {
    return extensionVersion;
  },

  /**
   * Get current connection status
   * @returns {{ status: string, details: object, connected: boolean }}
   */
  getConnectionStatus() {
    return {
      status: connectionStatus,
      details: connectionStatusDetails,
      connected: connectionStatus === CONNECTION_STATUS.CONNECTED
    };
  },

  /**
   * Register a callback for connection status changes
   * @param {function} callback - Called with (status, details, connected)
   * @returns {function} - Cleanup function to unregister the callback
   */
  onConnectionStatusChange(callback) {
    connectionStatusCallbacks.add(callback);

    // Return cleanup function
    return () => {
      connectionStatusCallbacks.delete(callback);
    };
  },

  /**
   * Check if connection is currently healthy
   * @returns {boolean}
   */
  isConnected() {
    return connectionStatus === CONNECTION_STATUS.CONNECTED && extensionAvailable === true;
  },

  /**
   * Get token status from extension
   * @returns {Promise<{hasToken: boolean, isValid: boolean, expiresAt: number|null}>}
   */
  async getTokenStatus() {
    if (!await this.isAvailable()) {
      throw new Error('Extension not available');
    }

    const response = await sendMessage('SHOWLARK_REQUEST_TOKEN_STATUS');
    return {
      hasToken: response.hasToken,
      isValid: response.isValid,
      expiresAt: response.expiresAt
    };
  },

  /**
   * Start playlist generation
   * @param {object} options - Generation options
   * @param {string} options.genre - Music genre
   * @param {string} options.theme - Optional theme
   * @param {number} options.songCount - Number of songs
   * @param {string} options.audioType - 'instrumental' or 'vocal'
   * @returns {Promise<{task: object}>}
   */
  async startGeneration(options) {
    if (!await this.isAvailable()) {
      throw new Error('Extension not available');
    }

    const response = await sendMessage('SHOWLARK_START_GENERATION', { options });
    return { task: response.task };
  },

  /**
   * Get current task status
   * @returns {Promise<object|null>}
   */
  async getTaskStatus() {
    if (!await this.isAvailable()) {
      throw new Error('Extension not available');
    }

    const response = await sendMessage('SHOWLARK_GET_TASK_STATUS');
    return response.task;
  },

  /**
   * Cancel current generation
   * @returns {Promise<void>}
   */
  async cancelGeneration() {
    if (!await this.isAvailable()) {
      throw new Error('Extension not available');
    }

    await sendMessage('SHOWLARK_CANCEL_GENERATION');
  },

  /**
   * Resume generation after CAPTCHA
   * @returns {Promise<{task: object}>}
   */
  async resumeAfterCaptcha() {
    if (!await this.isAvailable()) {
      throw new Error('Extension not available');
    }

    const response = await sendMessage('SHOWLARK_RESUME_AFTER_CAPTCHA');
    return { task: response.task };
  },

  /**
   * Set progress callback for generation updates
   * @param {function} callback - Progress callback
   */
  onProgress(callback) {
    progressCallback = callback;
  },

  /**
   * Clear progress callback
   */
  clearProgressCallback() {
    progressCallback = null;
  },

  // =========================================================================
  // Library Browsing (Suno -> Visualizer)
  // =========================================================================

  /**
   * Browse user's Suno library (v3 cursor-based pagination)
   * @param {object} options - Pagination and filter options
   * @param {string|null} options.cursor - Cursor for pagination (null for first page)
   * @param {number} options.pageSize - Items per page (default 20)
   * @param {string|null} options.searchText - Server-side text search query
   * @param {string} options.filter - Filter type: 'all', 'liked', 'created'
   * @returns {Promise<{songs: object[], hasMore: boolean, total: number, nextCursor: string|null}>}
   */
  async browseLibrary(options = {}) {
    if (!await this.isAvailable()) {
      throw new Error('Extension not available');
    }

    const response = await sendMessage('SHOWLARK_BROWSE_LIBRARY', { options });
    return {
      songs: response.songs || [],
      hasMore: response.hasMore || false,
      total: response.total || 0,
      nextCursor: response.nextCursor || null
    };
  },

  /**
   * Search user's Suno library (delegates to browseLibrary with searchText)
   * @param {string} query - Search query
   * @param {object} options - Pagination options
   * @returns {Promise<{songs: object[], hasMore: boolean, nextCursor: string|null}>}
   */
  async searchLibrary(query, options = {}) {
    return this.browseLibrary({ ...options, searchText: query });
  },

  /**
   * Get single song metadata
   * @param {string} clipId - The clip ID
   * @returns {Promise<object>} Song metadata
   */
  async getSongMetadata(clipId) {
    if (!await this.isAvailable()) {
      throw new Error('Extension not available');
    }

    const response = await sendMessage('SHOWLARK_GET_SONG_METADATA', { clipId });
    return response.song;
  },

  /**
   * Get aligned (time-synced) lyrics for a song
   * @param {string} clipId - The clip ID
   * @returns {Promise<object|null>} Aligned lyrics object or null if unavailable
   */
  async getAlignedLyrics(clipId) {
    if (!await this.isAvailable()) {
      return null;
    }

    try {
      const response = await sendMessage('SHOWLARK_GET_ALIGNED_LYRICS', { clipId });
      return response.alignedLyrics || null;
    } catch (error) {
      console.error('[ExtensionBridge] getAlignedLyrics error:', error);
      return null;
    }
  },

  // =========================================================================
  // Download/Export (Visualizer -> Filesystem)
  // =========================================================================

  /**
   * Get CDN download URL for a song
   * @param {string} clipId - The clip ID
   * @param {string} format - 'mp3' or 'wav'
   * @returns {Promise<string>} The download URL
   */
  async getDownloadUrl(clipId, format = 'mp3') {
    if (!await this.isAvailable()) {
      throw new Error('Extension not available');
    }

    const response = await sendMessage('SHOWLARK_GET_DOWNLOAD_URL', { clipId, format });
    return response.url;
  },

  /**
   * Export a song to user's filesystem via browser download
   * @param {string} clipId - The clip ID
   * @param {string} format - 'mp3' or 'wav'
   * @param {string} filename - Suggested filename (without extension)
   * @returns {Promise<{success: boolean, downloadId?: number, error?: string}>}
   */
  async exportSong(clipId, format = 'mp3', filename) {
    if (!await this.isAvailable()) {
      throw new Error('Extension not available');
    }

    const response = await sendMessage('SHOWLARK_EXPORT_SONG', {
      clipId,
      format,
      filename: filename || clipId
    });
    return {
      success: response.success,
      downloadId: response.downloadId,
      filename: response.filename
    };
  },

  /**
   * Export multiple songs to user's filesystem
   * Progress updates are sent via showlarkExportProgress event
   * @param {Array<{clipId: string, title: string}>} songs - Songs to export
   * @param {string} format - 'mp3' or 'wav'
   * @returns {Promise<{success: boolean, completed: number, failed: number, errors: string[]}>}
   */
  async exportBatch(songs, format = 'mp3') {
    if (!await this.isAvailable()) {
      throw new Error('Extension not available');
    }

    const response = await sendMessage('SHOWLARK_EXPORT_BATCH', { songs, format });
    return {
      success: response.success,
      completed: response.completed || 0,
      failed: response.failed || 0,
      errors: response.errors || []
    };
  },

  // =========================================================================
  // Generic Fetch (Schema-Driven API)
  // =========================================================================

  /**
   * Execute a generic fetch request through the extension
   * Allows the Visualizer to specify API endpoints while extension handles auth
   *
   * @param {object} request - Request configuration
   * @param {string} request.url - Target URL (must be in extension whitelist)
   * @param {string} [request.method='GET'] - HTTP method
   * @param {object} [request.headers={}] - Additional headers
   * @param {*} [request.body] - Request body
   * @param {boolean} [request.authRequired=false] - Add Authorization header
   * @param {boolean} [request.addDeviceHeaders=false] - Add device headers
   * @param {number} [request.timeout=30000] - Request timeout in ms
   *
   * @returns {Promise<{success: boolean, status?: number, headers?: object, data?: *, error?: string}>}
   */
  async genericFetch(request) {
    if (!await this.isAvailable()) {
      throw new Error('Extension not available');
    }

    const response = await sendMessage('SHOWLARK_GENERIC_FETCH', { request });

    // Return the full response object (success, status, data, error, etc.)
    return response;
  },

  /**
   * Execute a generic fetch using an endpoint from api-config.js
   * Convenience wrapper that builds the request from endpoint config
   *
   * @param {object} endpoint - Endpoint configuration from api-config.js
   * @param {object} [options={}] - Additional options
   * @param {object} [options.body] - Request body (overrides endpoint)
   * @param {object} [options.headers] - Additional headers (merged with endpoint)
   *
   * @returns {Promise<{success: boolean, status?: number, headers?: object, data?: *, error?: string}>}
   */
  async fetchEndpoint(endpoint, options = {}) {
    if (!endpoint || !endpoint.url) {
      throw new Error('Invalid endpoint configuration');
    }

    const request = {
      url: endpoint.url,
      method: endpoint.method || 'GET',
      headers: {
        ...(endpoint.headers || {}),
        ...(options.headers || {})
      },
      body: options.body !== undefined ? options.body : endpoint.body,
      authRequired: endpoint.authRequired,
      addDeviceHeaders: endpoint.addDeviceHeaders,
      timeout: endpoint.timeout || 30000
    };

    return this.genericFetch(request);
  }
};

// Export for external use
export default extensionBridge;
